// TabContent1.js
import { useTheme } from '@emotion/react';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Box, ButtonBase, InputBase, TextField, Typography } from '@mui/material';
import cx from "classnames";
import { t } from "i18next";
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from "yup";
import { useGetReturnReasonsSelectQuery } from '../../../../../newapi/inbound/returnsSlice';
import { useGetProductsSelectQuery } from '../../../../../newapi/warehouse/productSlice';
import { tokens } from '../../../../../theme';
import { errorMapWithArrayProcessor } from '../../../../../utils/helpers';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { DropdownBaseV2 } from '../../../../theme/dropdowns/Dropdown';
import { StyledDropdown } from '../../../../theme/dropdowns/StyledDropdown';
import NumberInput from '../../../../theme/inputs/NumberInput';
import ShSwitch from "../../../../theme/inputs/ShSwitch";
import Subheading from '../../../../theme/text/Subheading';
import Text from '../../../../theme/text/Text';
import { ReactComponent as BarcodeSvg } from "../../../../../styles/svg/barcode.svg";
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import SHQuickAction from '../../../../theme/Modal/SHQuickAction';
import { ProductHoverDetails } from '../../../../../environments/warehouse/echeck2/orders/OrdersEcheckView2';
import RoomIcon from '@mui/icons-material/Room';
import ReturnLineLocation from '../../../shared/ReturnLineLocation';



/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.localData };
    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const positiveRef = useRef(null);
    const negativeRef = useRef(null);

    const {data: reasons,isReturnReasonsSusccess} = useGetReturnReasonsSelectQuery({
        relationId:data.relationId
    });

    const { data: products } = useGetProductsSelectQuery(
        {
            relationId: data.relationId,
            ignoreVirtual: true,
        },
        { skip: !data.relationId }
    );

    const [productOptions, setProductOptions] = useState([]);
    useEffect(() => {
        if (products && products.length > 0) {
            const options = products.map(x => {
                return {
                    ...x,
                    barcodeArray: x.barcodes?.split(", ")
                }
            });

            setProductOptions(options);
        }
    }, [products]);

    // console.log(props)

    const [returnRequest, setReturnRequest] = useState({
        returnLines: []
    })

    useEffect(() => {
        if(props.localData.returnLines) {
            setReturnRequest(prev => ({
                ...prev,
                returnLines:[...props.localData.returnLines]
            }))
        }
    },[])

    const [validationErrors, setValidationErrors] = useState({});
    const updateReturnLine = (index, updates) => {
        setReturnRequest(prev => {
            const updated = [...prev.returnLines];

            updated[index] = {
                ...updated[index],
                ...updates,
            };

            return {
                ...prev,
                returnLines: updated,
            };
        });
    }
    const deleteReturnLine = (index) => {
        setReturnRequest(prev => {
            const updated = prev.returnLines.filter((_, i) => i !== index);

            return {
                ...prev,
                returnLines: updated,
            };
        });
    }; 
    
    const handleErrors = (errors) => {
        const { fieldErrors, generalErrors } = errorMapWithArrayProcessor(errors, /returnLines\[(\d+)\]\.(qty|retourreasonId|warehouseLocationId)/);
        setValidationErrors({ fieldErrors, generalErrors });
    };


    useEffect(() => {
        dispatch(setTabErrors({ }));
        props.setLocalData({
            ...props.localData,
            returnLines: returnRequest.returnLines,
        })
    }, [returnRequest])

    useEffect(() => {
        handleErrors(props.errors);
    }, [props.errors])

    return (
        <Box
            sx={{
                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                borderTopLeftRadius:16,
                borderTopRightRadius:16,
                display:"flex",
                flexDirection:"column",
                gap:1,
            }}
        >
            {/* {console.log(products)} */}

            <Box
                sx={{
                    display:"flex",
                    gap:1.5,
                    height:40,
                    width:1,
                    background:colors.grey[100],
                    borderRadius:5,
                    p:0.5,
                }}
            >
                <Box
                    sx={{
                        height:32,
                        px:2,
                        borderRadius:4,
                        background:colors.grey[200],
                        "& .echeck-barcode-svg" : {
                            width:32,
                            height:32,
                            // width:"380px !important",
                            "& path" : {
                                fill: colors.txt["secondary"]
                            }
                        }
                    }}
                >
                    <BarcodeSvg 
                        className="echeck-barcode-svg"
                    />
                </Box>
                <InputBase
                    inputRef={inputRef}
                    autoFocus
                    placeholder={t("scan_an_order")}
                    sx={{
                        height:32,
                        flex:1,
                        color:colors.txt["primary"]
                    }}
                    onKeyDown={(e) => {
                        if(e.key === "Enter") {
                            const product = productOptions?.find(product => product.barcodeArray.includes(e.target.value));
                            if(product) {
                                setReturnRequest((prev) => {
                                    let updated = [...prev.returnLines];

                                    updated.push({
                                        sku:product.sku,
                                        barcodes:product.barcodes,
                                        description:product.description,
                                        productId: product.id,
                                        product:product,
                                        qty: "",
                                        internalRemark: "",
                                        acceptedCount:"",
                                        rejectedCount:"",
                                        internalRemark:"",
                                    });

                                    return {
                                        ...prev,
                                        returnLines:updated,
                                    };
                                });
                                e.target.value = "";
                            }
                        }
                    }}
                />
                <DropdownBaseV2
                    // key={data.relation}
                    name={"relation"}
                    onChange={(selected) => {
                        let product = products[selected];
                        setReturnRequest((prev) => {
                            let updated = [...prev.returnLines];

                            updated.push({
                                sku:product.sku,
                                barcodes:product.barcodes,
                                description:product.description,
                                productId: product.id,
                                product:product,
                                qty: "",
                                internalRemark: "",
                                acceptedCount:"",
                                rejectedCount:"",
                                internalRemark:"",
                            });

                            return {
                                ...prev,
                                returnLines:updated,
                            };
                        });
                        inputRef.current.focus();
                    }}
                    displayName={"combined"}
                    options={products}
                    closeOnSelection
                    resetOnSelection
                    multiple={false}
                    selected={-1}
                    searchOptions={{ enabled: true, placeholder: t("search_product_placeholder"), searchables: ["barcodes", "sku", "description"] }}
                >
                    {({anchorEl,onClick}) => (
                        <ButtonBase
                            onClick={onClick}
                            sx={{
                                backgroundColor : `${theme.palette.mode === "dark" ?colors.txt["secondary"] : colors.grey[400]}`,
                                borderRadius:4,
                                display:"flex",
                                height:32,
                                // width:32,
                                px:2,
                                display:'flex',
                                alignItems:'center',
                                justifyContent:'center',
                                fontFamily:"Roboto, sans-serif",
                                fontWeight:500,
                                fontSize:14,
                                color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"]
                            }}
                        >
                            {t("choose")}
                        </ButtonBase>
                    )}
                </DropdownBaseV2>
            </Box>
            {returnRequest.returnLines.map((line,idex) => {
                
                const acceptedKey = `returnLines[${idex}].acceptedCount`;
                const rejectedKey = `returnLines[${idex}].rejectedCount`;
                const qtyKey = `returnLines[${idex}].qty`;

                // Accepted is derived from rejected on save, so the location has to be
                // asked for whenever part of the line ends up back in stock - not only
                // when the accepted field itself was typed in.
                const qtyValue = parseInt(line.qty) || 0;
                let acceptedValue = parseInt(line.acceptedCount);
                if (isNaN(acceptedValue)) {
                    acceptedValue = qtyValue - (parseInt(line.rejectedCount) || 0);
                }
                return(
                    <Box
                        sx={{
                            width:1,
                            borderRadius:4,
                            color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400],
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                            p:2,
                        }}
                    >
                        <Box
                            sx={{
                                display:'flex',
                                justifyContent:'space-between',
                                alignItems:'center',
                            }}
                        >
                            <SHQuickAction
                                component={<ProductHoverDetails productId={line.product?.id} product={line.product}/>}
                                disableScrollLock={true}
                                anchorOrigin={{
                                    vertical:'center',
                                    horizontal:"right"
                                }}
                                transformOrigin={{
                                    vertical:'center',
                                    horizontal:"left"
                                }}
                                sx={{
                                    modal:{
                                        body:{
                                            ml:2,
                                        }
                                    }
                                    // ml:1.5,
                                }}
                                                disableAutoFocus
                                                disableEnforceFocus
                            >
                                {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                                    <ButtonBase
                                        aria-owns={anchorEl ? 'simple-popover' : undefined}
                                        aria-haspopup="true"
                                        onMouseEnter={onMouseEnter}
                                        onMouseLeave={onMouseLeave}
                                        onTouchStart={onTouchStart} 
                                        sx={{
                                            flex:1,
                                            display:'flex',
                                            gap:1.5,
                                            alignItems:'flex-start',
                                            justifyContent:'flex-start',
                                            color:colors.txt["secondary"]
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display:"flex",
                                                flexDirection:"column",
                                            }}
                                        >
                                            <Text bold>{line.sku}</Text>
                                            <Text>{line.description}</Text>
                                        </Box>
                                        <RoomIcon/>
                                    </ButtonBase>
                                )}
                            </SHQuickAction>
                            <Box>
                               

                                <ButtonBase
                                    tabIndex={-1}
                                    onClick={() => {
                                        deleteReturnLine(idex);
                                        inputRef.current.focus();
                                    }}
                                    sx={{
                                        height:32,
                                        width:32,
                                        ml:1.5,
                                        borderRadius:5,
                                        color:colors.red[400],
                                        background:colors.red[100],
                                    }}
                                >
                                    <DeleteOutlinedIcon fontSize='small'/>
                                </ButtonBase>
                            </Box>

                        </Box>
                        <Box
                            sx={{
                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                height:32,
                                borderRadius:4,
                                display:"flex",
                                alignItems:"center",
                                pl:0.5,
                                mt:2,
                                mb:1,
                            }}
                        >
                            <Box
                                sx={{ 
                                    background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                    height:24,
                                    borderRadius:3,
                                    px:1.5,
                                }}
                            >
                                <Text>{t("total_amount")}</Text>
                            </Box>
                             <InputBase
                                    value={line.qty}
                                    sx={{
                                        flex:1,
                                        height:32,
                                        px:2,
                                        color:colors.txt["primary"],
                                    }}
                                    onChange={(e) => {
                                        const input = e.target.value;
                                        if (/^\d*$/.test(input)) {
                                            if (input === '' || /^[1-9]\d*$/.test(input)) {
                                                updateReturnLine(idex, {
                                                    qty: input,
                                                });
                                            }
                                        }
                                    }}
                                />
                        </Box>
                        <Box
                            sx={{
                                p:1.5,
                                position:"relative",
                                borderRadius:4,
                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[200]}`,
                            }}
                        >
                            {(!line.qty || line.qty <= 0) &&
                                <Box
                                    sx={{
                                        position:"absolute",
                                        top:0,
                                        left:0,
                                        right:0,
                                        bottom:0,
                                        zIndex:1,
                                        borderRadius:4,
                                        backdropFilter:"blur(16px)",
                                        display:"flex",
                                        alignItems:"center",
                                        justifyContent:"center",
                                    }}
                                >
                                    <Text bold>{t("fill_in_total_amount_first")}</Text>
                                </Box>
                            }
                            {line.qty > -1 &&
                                <Box
                                    sx={{
                                        display:"flex",
                                        gap:2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:'column',
                                            flex:1,
                                            maxWidth:300,
                                        }}
                                    >
                                        <Text bold variant={"green"}>{t("accepted")}</Text>
                                        <NumberInput
                                            min={0}
                                            inputRef={positiveRef}
                                            key={line.acceptedCount}
                                            value={line.acceptedCount}
                                            onlyPositive
                                            change={(amount) => {
                                                updateReturnLine(idex, {
                                                    acceptedCount: parseInt(amount),
                                                });
                                                // if(positiveRef.current) {
                                                //     positiveRef.current.focus();
                                                // }
                                            }}
                                            max={line.qty}
                                            inputSX={{
                                                mt:1,
                                                height:32,
                                                borderRadius:4,
                                                background:colors.green[100]
                                            }}
                                        />

                                        {validationErrors.generalErrors?.[acceptedKey] && (
                                            <Text variant="red">
                                                {t("amount_must_be_at_minimum_0")}
                                            </Text>
                                        )}

                                    </Box>

                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:'column',
                                            flex:1,
                                            maxWidth:300,
                                        }}
                                    >
                                        <Text bold variant={"red"}>{t("rejected")}</Text>
                                        <NumberInput
                                            inputRef={negativeRef}
                                            min={0}
                                            key={line.rejectedCount}
                                            value={line.rejectedCount}
                                            onlyPositive
                                            change={(amount) => {
                                                updateReturnLine(idex, {
                                                    rejectedCount: parseInt(amount),
                                                });
                                                // if(negativeRef.current) {
                                                //     negativeRef.current.focus();
                                                // }
                                            }}
                                            max={line.qty}
                                            inputSX={{
                                                mt:1,
                                                height:32,
                                                borderRadius:4,
                                                background:colors.red[100]
                                            }}
                                        />

                                        {validationErrors.generalErrors?.[rejectedKey] && (
                                            <Text variant="red">
                                                {t("amount_must_be_at_minimum_0")}
                                            </Text>
                                        )}

                                    </Box>
                                </Box>
                            }
                            <InputBase
                                multiline
                                rows={4}
                                value={line.internalRemark}
                                onChange={(e) => {
                                    updateReturnLine(idex, {
                                        internalRemark: e.target.value,
                                    });
                                }}
                                placeholder={t("remark")}
                                sx={{
                                    mt:1,
                                    width: '100%',
                                    fontSize: 14,
                                    px:2,
                                    py:1.5,
                                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                    borderRadius: 5,
                                }}
                            />

                            {acceptedValue > 0 &&
                                <ReturnLineLocation
                                    productId={line.productId}
                                    value={line}
                                    fieldError={validationErrors.fieldErrors?.[idex]}
                                    onChange={(updates) => updateReturnLine(idex, updates)}
                                />
                            }
                        </Box>

                     
                    </Box>
                )
            })}

            
        </Box>
    );
};


const ReturnLines = (props) => {
    const { t } = useTranslation();

    const conditionsSchema = yup.array().of(
        yup.object({
            qty: yup
                .number()
                .moreThan(0, 'amount_must_be_greater_than_0')
                .required("amount_required"),
            acceptedCount: yup
                .number()
                .min(0, 'accepted_count_must_be_0_or_more')
                .required('accepted_count_required'),
            rejectedCount: yup
                .number()
                .min(0, 'rejected_count_must_be_0_or_more')
                .required('rejected_count_required'),
            // Accepting stock onto a product with no home requires binding a location.
            warehouseLocationId: yup.number().nullable().test(
                'location-required',
                'choose_a_location_to_book_the_items_onto',
                function (value) {
                    const { hasReturnLocation, acceptedCount } = this.parent;
                    if (hasReturnLocation === false && parseInt(acceptedCount) > 0) {
                        return typeof value === 'number' && value > 0;
                    }
                    return true;
                }
            ),
        })
      ).min(1, 'conditions_not_valid');
      
    const schema = yup.object({
        returnLines: conditionsSchema
      });

    const data = useSelector((state) => state.progressiontabs.data);
    const [localData, setLocalData] = useState(data);

    useEffect(() => {
        setLocalData(data);
    },[data]);
    
    const handleBeforeSave = (dataToSave) => {
        const updatedLines = dataToSave.returnLines.map(line => {
            const qty = parseInt(line.qty) || 0;
            const accepted = line.acceptedCount !== "" && line.acceptedCount !== undefined ? parseInt(line.acceptedCount) : null;
            const rejected = line.rejectedCount !== "" && line.rejectedCount !== undefined ? parseInt(line.rejectedCount) : null;

            // Only one filled in: derive the other from qty
            if (accepted !== null && rejected === null) {
                return { ...line, rejectedCount: qty - accepted };
            }
            if (rejected !== null && accepted === null) {
                return { ...line, acceptedCount: qty - rejected };
            }
            return line;
        });

        const result = { ...dataToSave, returnLines: updatedLines };
        setLocalData(result);
        return result;
    };

    return (
        <ProgressionTab
            tabId="tabReturnLines"
            subtitle={t("return_lines")}
            // nextTab={"tabShipments"}
            step={2}
            maxSteps={2}
            schema={schema}
            updateDataWithValidation={localData}
            onBeforeSave={handleBeforeSave}
        >
            <Content localData={localData} setLocalData={setLocalData}/>
        </ProgressionTab>
    )
}

export default ReturnLines;
